// External imports
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

// Internal imports
import { useAddToList } from "./use-lists";
import { Restaurant } from "@/features/dashboard/pages/restaurants/types/restaurants.types";

/**
 * Hook for adding a restaurant to a specific list
 * Handles the integration between restaurants and lists
 */
export const useAddRestaurantToList = () => {
  const queryClient = useQueryClient();
  const addToList = useAddToList();

  return useMutation({
    mutationFn: async ({ restaurant, listId }: { restaurant: Restaurant; listId: string }) => {
      return await addToList.mutateAsync({
        listId,
        restaurantId: restaurant.id,
      });
    },
    onSuccess: (_, variables) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ["lists"] });
      queryClient.invalidateQueries({ queryKey: ["lists", variables.listId] });

      toast.success(`Added "${variables.restaurant.name}" to list!`);
    },
    onError: (error: Error, variables) => {
      console.error("Failed to add restaurant to list:", error);

      if (error.message.includes("already in this list")) {
        toast.error(`"${variables.restaurant.name}" is already in this list`);
      } else {
        toast.error(`Failed to add "${variables.restaurant.name}" to list. Please try again.`);
      }
    },
  });
};

/**
 * Hook for bulk adding a restaurant to multiple lists
 */
export const useBulkAddRestaurantToLists = () => {
  const queryClient = useQueryClient();
  const addToList = useAddToList();

  return useMutation({
    mutationFn: async ({ restaurant, listIds }: { restaurant: Restaurant; listIds: string[] }) => {
      const promises = listIds.map(listId =>
        addToList.mutateAsync({ listId, restaurantId: restaurant.id })
      );
      return await Promise.allSettled(promises);
    },
    onSuccess: (results, variables) => {
      const successCount = results.filter(result => result.status === 'fulfilled').length;
      const failureCount = results.length - successCount;

      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ["lists"] });
      variables.listIds.forEach(listId => {
        queryClient.invalidateQueries({ queryKey: ["lists", listId] });
      });

      if (successCount > 0) {
        toast.success(`Added "${variables.restaurant.name}" to ${successCount} list${successCount > 1 ? 's' : ''}!`);
      }

      if (failureCount > 0) {
        toast.error(`Failed to add to ${failureCount} list${failureCount > 1 ? 's' : ''}`);
      }
    },
    onError: (error: Error, variables) => {
      console.error("Failed to add restaurant to lists:", error);
      toast.error(`Failed to add "${variables.restaurant.name}" to lists. Please try again.`);
    },
  });
};